import { LoadingScreen } from "@/components/LoadingScreen";
import { ArticleOutline } from "@/components/editor/ArticleOutline";
import { ArticleFAQ } from "@/components/editor/ArticleFAQ";
import { ArticleStats } from "@/components/editor/ArticleStats";
import { EditorHeader } from "@/components/editor/EditorHeader";
import { EditorSEO } from "@/components/editor/EditorSEO";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { DatePicker } from "@/components/ui/date-picker";
import { Label } from "@/components/ui/label";
import { useBrand } from "@/contexts/brand-context";
import { pb } from "@/lib/pocketbase";
import { ArticlePost, Outline, FAQ } from "@/types/article";
import {
  Integrations,
  IntegrationsTypeOptions,
  Posts,
  PostsDetail,
  PostsStatusOptions,
} from "@/types/pocketbase-types";
import {
  Edit,
  ListOrdered,
  Search,
  HelpCircle,
  Image,
  RefreshCw,
  Calendar,
  Loader2,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { PlateEditor } from "./PlateEditor";
import { TElement } from "@udecode/plate";
import { useIntegrations } from "@/contexts/integrations-context";
import { shopifyService } from "@/services/shopify";
import { astroService } from "@/services/astro";
import { AstroIntegration } from "@/types/integration";
import { SchedulingService } from "@/services/scheduling";
import { format, startOfTomorrow } from "date-fns";

// Constants
const COOLDOWN_BASIC = 30 * 1000; // 30 seconds for basic regeneration
const COOLDOWN_CUSTOM = 60 * 1000; // 60 seconds for custom visual styles

// Visual style options matching backend defaults
const VISUAL_STYLES = [
  { value: "auto", label: "Auto (Random)" },
  { value: "photorealistic", label: "Photorealistic" },
  { value: "flat illustration", label: "Flat Illustration" },
  { value: "3d rendered", label: "3D Rendered" },
  { value: "abstract", label: "Abstract" },
  { value: "vector art", label: "Vector Art" },
  { value: "hand-drawn sketch", label: "Hand-drawn Sketch" },
  { value: "watercolor", label: "Watercolor" },
  { value: "cutout collage", label: "Cutout Collage" },
];

// Rate limiting helper function
const getCooldownPeriod = (visualStyle: string) => {
  return visualStyle && visualStyle !== "auto"
    ? COOLDOWN_CUSTOM
    : COOLDOWN_BASIC;
};

const checkRateLimit = (lastRegenerationTime: number, visualStyle: string) => {
  const now = Date.now();
  const cooldownPeriod = getCooldownPeriod(visualStyle);
  const remaining = cooldownPeriod - (now - lastRegenerationTime);
  return {
    canRegenerate: remaining <= 0,
    remainingTime: Math.max(0, remaining),
    remainingSeconds: Math.ceil(Math.max(0, remaining) / 1000),
  };
};

export const DetailView = () => {
  const { articleId } = useParams<{ articleId: string }>();
  const [post, setPost] = useState<ArticlePost | null>(null);
  const [postDetail, setPostDetail] = useState<PostsDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState("editor");
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [isRegeneratingImage, setIsRegeneratingImage] = useState(false);
  const [lastRegenerationTime, setLastRegenerationTime] = useState<number>(0);
  const [cooldownRemaining, setCooldownRemaining] = useState<number>(0);
  const [selectedVisualStyle, setSelectedVisualStyle] =
    useState<string>("auto");
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [showScheduleModal, setShowScheduleModal] = useState(false);
  const [selectedScheduleDate, setSelectedScheduleDate] = useState<Date>();
  const [scheduling, setScheduling] = useState(false);
  const [scheduleConflicts, setScheduleConflicts] = useState<
    { date: Date; posts: Posts[] }[]
  >([]);
  const { currentBrand } = useBrand();
  const { getIntegrationsByCategory, integrations } = useIntegrations();
  const navigate = useNavigate();
  const [cmsIntegrations, setCmsIntegrations] = useState<Integrations[] | null>(
    getIntegrationsByCategory("cms"),
  );
  const handleArticleChange = useCallback(
    async (
      contentState: TElement[],
      forceSave: boolean = false,
      htmlContent?: string,
    ) => {
      if (!postDetail) return;

      // Extract plain text from contentState for stats calculations
      const extractTextFromElements = (elements: TElement[]): string => {
        return elements
          .map((element) => {
            if (element.children) {
              return extractTextFromElements(element.children as TElement[]);
            }
            return (element as { text?: string }).text || "";
          })
          .join(" ")
          .replace(/\s+/g, " ")
          .trim();
      };

      const plainTextContent = extractTextFromElements(contentState);

      setPostDetail((prev) =>
        prev
          ? {
              ...prev,
              contentState,
              content: plainTextContent,
              ...(htmlContent && { htmlContent }),
            }
          : null,
      );
      if (forceSave) {
        const updateData: {
          contentState: TElement[];
          htmlContent?: string;
        } = {
          contentState,
        };
        if (htmlContent) {
          updateData.htmlContent = htmlContent;
        }
        await pb.collection("posts_detail").update(postDetail.id, updateData);
      }
    },
    [postDetail],
  );

  useEffect(() => {
    if (!articleId || !currentBrand?.id) return;
    setLoading(true);
    pb.collection("posts")
      .getOne<ArticlePost>(articleId, { expand: "posts_detail_via_post" })
      .then((data) => {
        if (data.brand !== currentBrand?.id) {
          navigate("/articles");
          return;
        }
        setPost(data);
        setPostDetail(data.expand.posts_detail_via_post[0]);
        setLoading(false);
      })
      .catch(() => {
        setError("Failed to load article");
        setLoading(false);
      });
    setCmsIntegrations(getIntegrationsByCategory("cms"));
  }, [articleId, currentBrand?.id, navigate]);

  useEffect(() => {
    if (integrations && integrations.length > 0) {
      setCmsIntegrations(getIntegrationsByCategory("cms"));
    }
  }, [integrations, getIntegrationsByCategory]);

  // Cooldown timer effect with dynamic periods
  useEffect(() => {
    if (lastRegenerationTime === 0) return;

    // const cooldownPeriod = getCooldownPeriod(selectedVisualStyle);

    const interval = setInterval(() => {
      const { remainingSeconds } = checkRateLimit(
        lastRegenerationTime,
        selectedVisualStyle,
      );
      setCooldownRemaining(remainingSeconds);

      if (remainingSeconds === 0) {
        clearInterval(interval);
      }
    }, 1000);

    // Initial update
    const { remainingSeconds } = checkRateLimit(
      lastRegenerationTime,
      selectedVisualStyle,
    );
    setCooldownRemaining(remainingSeconds);

    return () => clearInterval(interval);
  }, [lastRegenerationTime, selectedVisualStyle]);

  if (loading) {
    return (
      <div className="p-4 text-center">
        <LoadingScreen
          title="Fetching article..."
          description="Please wait while we load your article"
        />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-6 text-center">
        <p className="text-red-500 mb-4">{error}</p>
        <Button onClick={() => window.location.reload()}>Try Again</Button>
      </div>
    );
  }

  const updateOutline = async (outline: Outline) => {
    if (!postDetail || !outline || !articleId) return;
    try {
      const updatedPostDetail = { ...postDetail };
      updatedPostDetail.outline = outline;
      setPostDetail(updatedPostDetail);
      await pb.collection("posts_detail").update(postDetail.id, { outline });
    } catch (error) {
      toast.error("Failed to update section points");
    }
  };

  const updateFAQs = async (faqs: FAQ[]) => {
    if (!postDetail || !articleId) return;
    try {
      const updatedPostDetail = { ...postDetail };
      const outline = updatedPostDetail.outline as Outline;
      if (outline) {
        outline.faqs = faqs;
        updatedPostDetail.outline = outline;
        setPostDetail(updatedPostDetail);
        await pb.collection("posts_detail").update(postDetail.id, { outline });
      }
    } catch (error) {
      toast.error("Failed to update FAQs");
    }
  };

  const updatePost = async (data: Partial<ArticlePost>) => {
    if (!data || !articleId) return;
    try {
      setPost((prev) => (prev ? { ...prev, ...data } : null));
      await pb.collection("posts").update(articleId, data);
    } catch (error) {
      toast.error("Failed to update article");
    }
  };

  const handleSave = async (silent: boolean = false) => {
    if (!postDetail || !articleId) return;

    try {
      setSaving(true);
      const updateData: {
        contentState: object | null | "";
        htmlContent?: string;
      } = {
        contentState: postDetail.contentState,
      };

      // Include HTML content if available
      if (postDetail.htmlContent) {
        updateData.htmlContent = postDetail.htmlContent;
      }

      await pb.collection("posts_detail").update(postDetail.id, updateData);
      if (!silent) {
        toast.success("Article saved successfully");
      }
    } catch (error) {
      console.error("Error saving article:", error);
      if (!silent) {
        toast.error("Failed to save article");
      }
    } finally {
      setSaving(false);
    }
  };

  const publishToWordpress = async () => {
    if (
      !post ||
      !postDetail ||
      !postDetail.htmlContent ||
      !cmsIntegrations ||
      cmsIntegrations.length === 0
    ) {
      return;
    }

    // Find the WordPress integration
    const wordpressIntegration = cmsIntegrations.find(
      (integration) => integration.type === IntegrationsTypeOptions.Wordpress,
    );

    if (!wordpressIntegration) {
      throw new Error("WordPress integration not found");
    }

    // Simple request payload - backend handles all the complexity
    const publishData = {
      postId: post.id,
      brandId: post.brand,
    };

    // Publish to WordPress
    const response = await fetch(`${pb.baseURL}/api/v1/wordpress/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(publishData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to publish to WordPress: ${errorData.error || "Unknown error"}`,
      );
    }

    const publishDataResponse = await response.json();

    setPost((prev) =>
      prev
        ? {
            ...prev,
            status: PostsStatusOptions.Published,
            publishedId: publishDataResponse.post_id,
            publishedUrl: publishDataResponse.post_url,
            publishedAt: new Date().toISOString(),
          }
        : null,
    );
  };

  const publishToShopify = async () => {
    if (
      !post ||
      !postDetail ||
      !postDetail.htmlContent ||
      !cmsIntegrations ||
      cmsIntegrations.length === 0
    ) {
      return;
    }

    // Find the Shopify integration
    const shopifyIntegration = cmsIntegrations.find(
      (integration) => integration.type === IntegrationsTypeOptions.Shopify,
    );

    if (!shopifyIntegration) {
      throw new Error("Shopify integration not found");
    }

    // Publish to Shopify using the simplified service (only postId and brandId needed)
    const publishData = await shopifyService.publish({
      postId: post.id,
      brandId: post.brand,
    });

    // Update post data with Shopify response
    const postData = {
      status: PostsStatusOptions.Published,
      publishedId: publishData.article_id,
      publishedUrl: publishData.link,
      publishedAt: new Date().toISOString(),
    };

    setPost((prev) =>
      prev
        ? {
            ...prev,
            ...postData,
          }
        : null,
    );
  };

  const publishToAstro = async () => {
    if (
      !post ||
      !postDetail ||
      !postDetail.htmlContent ||
      !cmsIntegrations ||
      cmsIntegrations.length === 0
    ) {
      return;
    }

    // Find the Astro integration
    const astroIntegration = cmsIntegrations.find(
      (integration) => integration.type === IntegrationsTypeOptions.Astro,
    ) as AstroIntegration;

    if (!astroIntegration) {
      throw new Error("Astro integration not found");
    }

    // Publish to Astro using the simplified service (only postId and brandId needed)
    const publishData = await astroService.publish({
      postId: post.id,
      brandId: post.brand,
    });

    // Update post data with Astro response
    const postData = {
      status: PostsStatusOptions.Published,
      publishedId: publishData.file_path,
      publishedUrl: publishData.link,
      publishedAt: new Date().toISOString(),
    };

    setPost((prev) =>
      prev
        ? {
            ...prev,
            ...postData,
          }
        : null,
    );
  };

  const publishToSanity = async () => {
    if (
      !post ||
      !postDetail ||
      !postDetail.htmlContent ||
      !cmsIntegrations ||
      cmsIntegrations.length === 0
    ) {
      return;
    }

    // Find the Sanity integration
    const sanityIntegration = cmsIntegrations.find(
      (integration) => integration.type === IntegrationsTypeOptions.Sanity,
    );

    if (!sanityIntegration) {
      throw new Error("Sanity integration not found");
    }

    // Simple request payload - backend handles all the complexity
    const publishData = {
      postId: post.id,
      brandId: post.brand,
    };

    // Publish to Sanity
    const response = await fetch(`${pb.baseURL}/api/v1/sanity/publish`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: pb.authStore.token,
      },
      body: JSON.stringify(publishData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      throw new Error(
        `Failed to publish to Sanity: ${errorData.message || "Unknown error"}`,
      );
    }

    const publishDataResponse = await response.json();

    setPost((prev) =>
      prev
        ? {
            ...prev,
            status: PostsStatusOptions.Published,
            publishedId: publishDataResponse.document_id,
            publishedUrl: publishDataResponse.studio_url,
            publishedAt: new Date().toISOString(),
          }
        : null,
    );

    // Success message
    toast.success(
      `Published to Sanity successfully! Document ID: ${publishDataResponse.document_id}`,
    );
  };

  const handlePublish = async () => {
    if (
      !post ||
      !postDetail ||
      !postDetail.htmlContent ||
      !cmsIntegrations ||
      cmsIntegrations.length === 0
    ) {
      return;
    }

    // Save before publishing
    // await handleSave(true);

    try {
      setPublishing(true);
      if (!cmsIntegrations?.length) return;

      const activeIntegration = cmsIntegrations[0];

      if (activeIntegration.type === IntegrationsTypeOptions.Wordpress) {
        await publishToWordpress();
        toast.success("Article published successfully to WordPress!");
      } else if (activeIntegration.type === IntegrationsTypeOptions.Shopify) {
        await publishToShopify();
        toast.success("Article published successfully to Shopify!");
      } else if (activeIntegration.type === IntegrationsTypeOptions.Astro) {
        await publishToAstro();
        toast.success("Article published successfully to Astro!");
      } else if (activeIntegration.type === IntegrationsTypeOptions.Sanity) {
        await publishToSanity();
        // Success message is handled in publishToSanity
      } else {
        toast.error("Unsupported integration type");
      }
    } catch (error) {
      console.error("Error publishing article:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to publish article";
      toast.error(errorMessage);
    } finally {
      setPublishing(false);
    }
  };

  const handleSchedulePost = async () => {
    if (!post || !currentBrand?.id || !selectedScheduleDate) return;

    setScheduling(true);
    try {
      const result = await SchedulingService.scheduleWithConflictResolution(
        post.id,
        currentBrand.id,
        selectedScheduleDate,
        false, // Don't auto-resolve conflicts
      );

      if (result.success) {
        // Update local state
        setPost((prev) =>
          prev
            ? {
                ...prev,
                status: PostsStatusOptions.Scheduled,
                scheduleDate: selectedScheduleDate.toISOString(),
              }
            : null,
        );

        setShowScheduleModal(false);
        toast.success(
          `Article scheduled for ${format(selectedScheduleDate, "PPP")}`,
        );
      } else if (result.conflicts) {
        // Show conflicts to user
        setScheduleConflicts(result.conflicts);
        toast.error(result.error || "Scheduling conflict detected");
      } else {
        toast.error(result.error || "Failed to schedule article");
      }
    } catch (error) {
      console.error("Error scheduling article:", error);
      toast.error("Failed to schedule article");
    } finally {
      setScheduling(false);
    }
  };

  const handleScheduleWithDate = async (date: Date) => {
    if (!post || !currentBrand?.id) return;

    setScheduling(true);
    try {
      const result = await SchedulingService.scheduleWithConflictResolution(
        post.id,
        currentBrand.id,
        date,
        true, // Auto-resolve conflicts for inline scheduling
      );

      if (result.success) {
        // Update local state
        setPost((prev) =>
          prev
            ? {
                ...prev,
                status: PostsStatusOptions.Scheduled,
                scheduleDate: date.toISOString(),
              }
            : null,
        );

        toast.success(`Article scheduled for ${format(date, "PPP")}`);
      } else {
        toast.error(result.error || "Failed to schedule article");
      }
    } catch (error) {
      console.error("Error scheduling article:", error);
      toast.error("Failed to schedule article");
    } finally {
      setScheduling(false);
    }
  };

  const handleResolveConflicts = async () => {
    if (!post || !currentBrand?.id || !selectedScheduleDate) return;

    setScheduling(true);
    try {
      const result = await SchedulingService.scheduleWithConflictResolution(
        post.id,
        currentBrand.id,
        selectedScheduleDate,
        true, // Auto-resolve conflicts
      );

      if (result.success) {
        // Update local state
        setPost((prev) =>
          prev
            ? {
                ...prev,
                status: PostsStatusOptions.Scheduled,
                scheduleDate: selectedScheduleDate.toISOString(),
              }
            : null,
        );

        setShowScheduleModal(false);
        setScheduleConflicts([]);
        toast.success(
          `Article scheduled for ${format(selectedScheduleDate, "PPP")}. Conflicted posts have been rescheduled.`,
        );
      } else {
        toast.error(
          result.error || "Failed to resolve conflicts and schedule article",
        );
      }
    } catch (error) {
      console.error("Error resolving conflicts:", error);
      toast.error("Failed to resolve conflicts");
    } finally {
      setScheduling(false);
    }
  };

  const handleUnschedulePost = async () => {
    if (!post) return;

    setScheduling(true);
    try {
      const result = await SchedulingService.unschedulePost(post.id);

      if (result.success) {
        // Update local state
        setPost((prev) =>
          prev
            ? {
                ...prev,
                status: PostsStatusOptions.Draft,
                scheduleDate: undefined,
              }
            : null,
        );

        toast.success("Article unscheduled");
      } else {
        toast.error(result.error || "Failed to unschedule article");
      }
    } catch (error) {
      console.error("Error unscheduling article:", error);
      toast.error("Failed to unschedule article");
    } finally {
      setScheduling(false);
    }
  };

  const handleRegenerate = async () => {
    if (!post || !articleId || !currentBrand?.id) return;

    try {
      const response = await fetch(
        `${pb.baseURL}/api/v1/posts/regenerate/${articleId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: pb.authStore.token,
          },
          body: JSON.stringify({
            postDetailId: postDetail?.id,
            title: post.title,
            keyword: post.keyword,
            brandId: currentBrand.id,
          }),
        },
      );

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to regenerate article");
      }

      toast.success(
        "Article regeneration started! You can view progress from the articles list.",
      );

      // Navigate back to articles list where progress can be tracked
      navigate("/articles");
    } catch (error) {
      console.error("Error regenerating article:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to regenerate article";
      toast.error(errorMessage);
    }
  };

  const handleOpenRegenerateModal = () => {
    if (!post || !currentBrand?.id || isRegeneratingImage) return;

    // Check rate limiting before opening modal
    const { canRegenerate, remainingSeconds } = checkRateLimit(
      lastRegenerationTime,
      selectedVisualStyle,
    );
    if (!canRegenerate) {
      toast.error(
        `Please wait ${remainingSeconds} seconds before regenerating again`,
      );
      return;
    }

    setShowRegenerateModal(true);
  };

  const handleRegenerateImage = async () => {
    if (!post || !currentBrand?.id || isRegeneratingImage) return;

    // Enhanced rate limiting - double check to prevent race conditions
    const { canRegenerate, remainingSeconds } = checkRateLimit(
      lastRegenerationTime,
      selectedVisualStyle,
    );
    if (!canRegenerate) {
      toast.error(
        `Please wait ${remainingSeconds} seconds before regenerating again`,
      );
      return;
    }

    try {
      const now = Date.now();
      setIsRegeneratingImage(true);
      setShowRegenerateModal(false); // Close modal when starting regeneration
      setLastRegenerationTime(now); // Set immediately to prevent race conditions

      // Build request payload with optional visual style
      const requestPayload: {
        brandId: string;
        topic: string;
        keywords: string;
        type: string;
        provider: string;
        visualStyle?: string;
      } = {
        brandId: currentBrand.id,
        topic: post.title,
        keywords: post.keyword || "",
        type: "cover",
        provider: "gemini",
      };

      // Add visual style if selected (and not auto)
      if (selectedVisualStyle && selectedVisualStyle !== "auto") {
        requestPayload.visualStyle = selectedVisualStyle;
      }

      const response = await fetch(`${pb.baseURL}/api/v1/images/generate`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: pb.authStore.token,
        },
        body: JSON.stringify(requestPayload),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.message || "Failed to regenerate image");
      }

      const imageData = await response.json();

      // Update the post with the new featured image
      const updatedPost = { ...post, featuredImage: imageData.url };
      setPost(updatedPost);

      // Update in database
      await pb.collection("posts").update(post.id, {
        featuredImage: imageData.url,
      });

      toast.success("Image regenerated successfully!");
    } catch (error) {
      console.error("Error regenerating image:", error);
      const errorMessage =
        error instanceof Error ? error.message : "Failed to regenerate image";
      toast.error(errorMessage);
    } finally {
      setIsRegeneratingImage(false);
    }
  };

  return (
    <div className="flex flex-col h-full overflow-hidden -mr-[20px]">
      <EditorHeader
        title={post?.title || ""}
        onBack={() =>
          navigate("/articles", {
            state: {
              updatedPost:
                post?.status === PostsStatusOptions.Published
                  ? {
                      id: post.id,
                      status: post.status,
                    }
                  : null,
            },
          })
        }
        onSave={handleSave}
        onPublish={handlePublish}
        onScheduleWithDate={handleScheduleWithDate}
        onUnschedule={handleUnschedulePost}
        onTitleChange={updatePost}
        isSaving={saving}
        isPublishing={publishing || scheduling}
        isPublished={post?.status === PostsStatusOptions.Published}
        publishedUrl={post?.publishedUrl}
        postStatus={post?.status}
        scheduleDate={post?.scheduleDate}
        publishDisabled={
          !cmsIntegrations ||
          cmsIntegrations.length === 0 ||
          post?.status === PostsStatusOptions.Published
        }
      />
      <div className="flex flex-1 overflow-hidden">
        <Tabs
          value={activeTab}
          onValueChange={setActiveTab}
          className="w-full mt-4 flex flex-col h-full"
        >
          <div className="flex flex-1">
            <TabsList className="mb-2">
              <TabsTrigger value="editor" className="flex items-center">
                <Edit className="h-4 w-4 mr-2" />
                Content
              </TabsTrigger>
              {post?.featuredImage && (
                <TabsTrigger
                  value="featured-image"
                  className="flex items-center"
                >
                  <Image className="h-4 w-4 mr-2" />
                  Cover Image
                </TabsTrigger>
              )}
              <TabsTrigger value="outline" className="flex items-center">
                <ListOrdered className="h-4 w-4 mr-2" />
                Outline
              </TabsTrigger>
              <TabsTrigger value="seo" className="flex items-center">
                <Search className="h-4 w-4 mr-2" />
                SEO
              </TabsTrigger>
              <TabsTrigger value="faq" className="flex items-center">
                <HelpCircle className="h-4 w-4 mr-2" />
                FAQ
              </TabsTrigger>
            </TabsList>
          </div>

          {post && postDetail && (
            <TabsContent value="editor" className="mt-0 h-full">
              <PlateEditor
                postDetail={postDetail}
                title={post.title}
                keyword={post.keyword}
                onArticleChange={handleArticleChange}
              />
            </TabsContent>
          )}
          {postDetail && (
            <TabsContent value="outline" className="mt-0">
              <ArticleOutline
                outline={
                  postDetail && "outline" in postDetail
                    ? (postDetail.outline as Outline)
                    : null
                }
                onOutlineChange={updateOutline}
              />
            </TabsContent>
          )}
          {post && (
            <TabsContent value="seo" className="mt-0">
              <EditorSEO
                title={post.seoTitle}
                metaDescription={post.seoDescription}
                onSEODetailUpdate={updatePost}
              />
            </TabsContent>
          )}
          {postDetail && (
            <TabsContent value="faq" className="mt-0">
              <ArticleFAQ
                faqs={
                  postDetail && "outline" in postDetail
                    ? (postDetail.outline as Outline)?.faqs || []
                    : []
                }
                onFAQsChange={updateFAQs}
                brandId={post?.brand}
                keyword={post?.keyword}
                outline={postDetail.outline}
              />
            </TabsContent>
          )}
          {post?.featuredImage && (
            <TabsContent value="featured-image" className="mt-0">
              <div className="flex flex-col items-center justify-center p-6 space-y-4">
                <div className="relative max-w-2xl w-full">
                  <img
                    src={post.featuredImage}
                    alt={post.seoTitle || post.title || "Featured image"}
                    className="w-full h-auto rounded-lg shadow-lg object-cover"
                    style={{ maxHeight: "500px" }}
                  />
                </div>
                <div className="text-center space-y-4">
                  <div className="space-y-2">
                    <h3 className="text-lg font-semibold">
                      {post.seoTitle || post.title}
                    </h3>
                    {post.seoDescription && (
                      <p className="text-muted-foreground text-sm max-w-md">
                        {post.seoDescription}
                      </p>
                    )}
                  </div>

                  {/* Regenerate Modal */}
                  <Dialog
                    open={showRegenerateModal}
                    onOpenChange={setShowRegenerateModal}
                  >
                    <DialogTrigger asChild>
                      <Button
                        onClick={handleOpenRegenerateModal}
                        disabled={isRegeneratingImage || cooldownRemaining > 0}
                        variant="outline"
                        className="flex items-center justify-center gap-2 mx-auto"
                      >
                        <RefreshCw
                          className={`h-4 w-4 ${isRegeneratingImage ? "animate-spin" : ""}`}
                        />
                        {isRegeneratingImage
                          ? "Generating..."
                          : cooldownRemaining > 0
                            ? `Wait ${cooldownRemaining}s`
                            : "Regenerate Image"}
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-md">
                      <DialogHeader>
                        <DialogTitle>Regenerate Cover Image</DialogTitle>
                        <DialogDescription>
                          Choose a visual style for your new cover image.
                        </DialogDescription>
                      </DialogHeader>

                      <div className="space-y-4 py-4">
                        {/* Visual Style Selector */}
                        <div className="space-y-2">
                          <Label
                            htmlFor="visual-style"
                            className="text-sm font-medium"
                          >
                            Visual Style
                          </Label>
                          <Select
                            value={selectedVisualStyle}
                            onValueChange={setSelectedVisualStyle}
                          >
                            <SelectTrigger className="w-full">
                              <SelectValue placeholder="Select visual style..." />
                            </SelectTrigger>
                            <SelectContent>
                              {VISUAL_STYLES.map((style) => (
                                <SelectItem
                                  key={style.value}
                                  value={style.value}
                                >
                                  {style.label}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>

                        {/* Rate Limiting Info */}
                        {selectedVisualStyle &&
                          selectedVisualStyle !== "auto" && (
                            <p className="text-sm text-muted-foreground">
                              Custom visual styles have a 60-second cooldown
                              period.
                            </p>
                          )}
                      </div>

                      <DialogFooter className="flex gap-2">
                        <Button
                          variant="outline"
                          onClick={() => setShowRegenerateModal(false)}
                          disabled={isRegeneratingImage}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={handleRegenerateImage}
                          disabled={isRegeneratingImage}
                          className="flex items-center gap-2"
                        >
                          <RefreshCw
                            className={`h-4 w-4 ${isRegeneratingImage ? "animate-spin" : ""}`}
                          />
                          {isRegeneratingImage
                            ? "Generating..."
                            : "Generate Image"}
                        </Button>
                      </DialogFooter>
                    </DialogContent>
                  </Dialog>
                </div>
              </div>
            </TabsContent>
          )}
        </Tabs>
        {post && postDetail && (
          <ArticleStats
            postDetail={postDetail}
            post={post}
            seoDescription={post?.seoDescription || ""}
            keyword={post?.keyword || ""}
            brandUrl={currentBrand?.url}
            onRegenerate={handleRegenerate}
            isRegenerating={false}
          />
        )}
      </div>

      {/* Schedule Modal */}
      <Dialog open={showScheduleModal} onOpenChange={setShowScheduleModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Schedule Article</DialogTitle>
            <DialogDescription>
              Choose when you want this article to be published automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-sm font-medium mr-4">
                Publication Date
              </Label>
              <DatePicker
                selectedDate={selectedScheduleDate}
                onDateChange={setSelectedScheduleDate}
                placeholder="Select a date"
                minDate={startOfTomorrow()}
              />
            </div>

            {/* Show conflicts if any */}
            {scheduleConflicts.length > 0 && (
              <div className="space-y-2">
                <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3">
                  <div className="flex items-start space-x-2">
                    <div className="flex-shrink-0">
                      <svg
                        className="h-5 w-5 text-yellow-400"
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="text-sm font-medium text-yellow-800">
                        Scheduling Conflict
                      </h4>
                      <p className="text-sm text-yellow-700 mt-1">
                        {scheduleConflicts[0].posts.length} other post(s) are
                        scheduled for{" "}
                        {selectedScheduleDate &&
                          format(selectedScheduleDate, "PPP")}
                        . You can automatically reschedule them to the next
                        available dates.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <DialogFooter className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => {
                setShowScheduleModal(false);
                setScheduleConflicts([]);
              }}
              disabled={scheduling}
            >
              Cancel
            </Button>

            {scheduleConflicts.length > 0 ? (
              <Button
                onClick={handleResolveConflicts}
                disabled={scheduling || !selectedScheduleDate}
                className="flex items-center gap-2"
              >
                {scheduling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4" />
                    Resolve & Schedule
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={handleSchedulePost}
                disabled={scheduling || !selectedScheduleDate}
                className="flex items-center gap-2"
              >
                {scheduling ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Scheduling...
                  </>
                ) : (
                  <>
                    <Calendar className="h-4 w-4" />
                    Schedule Article
                  </>
                )}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DetailView;
